import React from 'react';
import {SOURCES_ROOT_VIEW, SOURCES_PRESET_FOLDERS} from './sourcesAdapter';
import {createSourcesFolder, removeSourcesFolder, renameSourcesFolder} from './sourcesApi';
import {clearSourcesCache} from './sourcesCacheAdapter';
import {removeItemsFromSources} from './sourcesListMutations';

const MAX_FOLDER_NAME_LENGTH = 256;
const MY_FILES_VIEW = Object.freeze({
    type: 'preset',
    id: 'my',
    name: 'My files',
    branch: 'my',
});

function resolveViewAfterFolderCreate(activeView) {
    if (activeView?.type === 'root') {
        return MY_FILES_VIEW;
    }

    if (activeView?.type === 'preset' && activeView.id === 'shared') {
        return MY_FILES_VIEW;
    }

    return activeView;
}

export default function useSourcesFolderActions({
    activeView,
    setActiveView,
    sources,
    setSources,
    userId,
    loadSources,
    onFolderCreated,
}) {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
    const [newFolderName, setNewFolderName] = React.useState('');
    const [createFolderError, setCreateFolderError] = React.useState(null);
    const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);

    const [folderMenuAnchorEl, setFolderMenuAnchorEl] = React.useState(null);
    const [selectedFolder, setSelectedFolder] = React.useState(null);

    const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
    const [renameFolderName, setRenameFolderName] = React.useState('');
    const [renameFolderError, setRenameFolderError] = React.useState(null);
    const [isRenamingFolder, setIsRenamingFolder] = React.useState(false);

    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
    const [removeFolderError, setRemoveFolderError] = React.useState(null);
    const [isRemovingFolder, setIsRemovingFolder] = React.useState(false);

    const trimmedFolderName = newFolderName.trim();
    const isFolderNameTooLong = trimmedFolderName.length > MAX_FOLDER_NAME_LENGTH;
    const isCreateFolderDisabled = !trimmedFolderName || isFolderNameTooLong || isCreatingFolder || !userId;

    const trimmedRenameFolderName = renameFolderName.trim();
    const isRenameFolderDisabled = !trimmedRenameFolderName
        || trimmedRenameFolderName.length > MAX_FOLDER_NAME_LENGTH
        || isRenamingFolder
        || !selectedFolder;

    const resetCreateFolderDialog = React.useCallback(() => {
        setIsCreateDialogOpen(false);
        setCreateFolderError(null);
        setNewFolderName('');
    }, []);

    const openCreateFolderDialog = React.useCallback(() => {
        resetCreateFolderDialog();
        setIsCreateDialogOpen(true);
    }, [resetCreateFolderDialog]);

    const closeCreateFolderDialog = React.useCallback(() => {
        if (isCreatingFolder) {
            return;
        }

        resetCreateFolderDialog();
    }, [isCreatingFolder, resetCreateFolderDialog]);

    const closeFolderMenu = React.useCallback(() => {
        setFolderMenuAnchorEl(null);
    }, []);

    const openFolderMenu = React.useCallback((event, folder) => {
        event.stopPropagation();
        setSelectedFolder(folder);
        setFolderMenuAnchorEl(event.currentTarget);
        setRenameFolderError(null);
        setRemoveFolderError(null);
    }, []);

    const openRenameDialog = React.useCallback(() => {
        setRenameFolderName(selectedFolder?.name || '');
        setRenameFolderError(null);
        setIsRenameDialogOpen(true);
        closeFolderMenu();
    }, [closeFolderMenu, selectedFolder]);

    const closeRenameDialog = React.useCallback(() => {
        if (isRenamingFolder) {
            return;
        }

        setIsRenameDialogOpen(false);
        setRenameFolderError(null);
        setRenameFolderName('');
    }, [isRenamingFolder]);

    const openRemoveDialog = React.useCallback(() => {
        setRemoveFolderError(null);
        setIsRemoveDialogOpen(true);
        closeFolderMenu();
    }, [closeFolderMenu]);

    const closeRemoveDialog = React.useCallback(() => {
        if (isRemovingFolder) {
            return;
        }

        setIsRemoveDialogOpen(false);
        setRemoveFolderError(null);
    }, [isRemovingFolder]);

    const handleCreateFolder = React.useCallback(async () => {
        if (isCreateFolderDisabled) {
            return;
        }

        setIsCreatingFolder(true);
        setCreateFolderError(null);

        try {
            const createdFolder = await createSourcesFolder({
                view: activeView,
                userId,
                name: trimmedFolderName,
            });

            const nextView = resolveViewAfterFolderCreate(activeView);
            const shouldSwitchView = JSON.stringify(nextView) !== JSON.stringify(activeView);
            if (shouldSwitchView) {
                setActiveView(nextView);
            }

            await loadSources(nextView, {suppressError: true});
            onFolderCreated?.(createdFolder, {activeView: nextView});

            resetCreateFolderDialog();
        } catch (err) {
            console.error('[SourcesSection] Failed to create folder:', err);
            setCreateFolderError(err?.message || 'Failed to create folder.');
        } finally {
            setIsCreatingFolder(false);
        }
    }, [
        activeView,
        isCreateFolderDisabled,
        loadSources,
        onFolderCreated,
        resetCreateFolderDialog,
        setActiveView,
        trimmedFolderName,
        userId,
    ]);

    const handleRenameFolder = React.useCallback(async () => {
        if (isRenameFolderDisabled || !selectedFolder) {
            return;
        }

        setIsRenamingFolder(true);
        setRenameFolderError(null);

        try {
            await renameSourcesFolder({
                folderId: selectedFolder.id,
                userId,
                name: trimmedRenameFolderName,
            });

            setIsRenameDialogOpen(false);
            await loadSources(activeView, {suppressError: true});
        } catch (err) {
            console.error('[SourcesSection] Failed to rename folder:', err);
            setRenameFolderError(err?.message || 'Failed to rename folder.');
        } finally {
            setIsRenamingFolder(false);
        }
    }, [activeView, isRenameFolderDisabled, loadSources, selectedFolder, trimmedRenameFolderName, userId]);

    const handleRemoveFolder = React.useCallback(async () => {
        if (!selectedFolder || isRemovingFolder) {
            return;
        }

        setIsRemovingFolder(true);
        setRemoveFolderError(null);

        try {
            await removeSourcesFolder({folderId: selectedFolder.id, userId});
            setSources(removeItemsFromSources(sources, {folderIds: [selectedFolder.id]}));
            clearSourcesCache(activeView).catch(() => {});
            setIsRemoveDialogOpen(false);
            loadSources(activeView, {suppressError: true}).catch(() => {});
        } catch (err) {
            console.error('[SourcesSection] Failed to remove folder:', err);
            setRemoveFolderError(err?.message || 'Failed to remove folder.');
        } finally {
            setIsRemovingFolder(false);
        }
    }, [activeView, isRemovingFolder, loadSources, selectedFolder, setSources, sources, userId]);

    const handleFolderClick = React.useCallback((folder) => {
        if (folder.isPreset) {
            setActiveView({type: 'preset', id: folder.id, name: folder.name, branch: folder.id});
            return;
        }

        const nextBranch = activeView.type === 'preset' ? activeView.branch : activeView.branch || null;
        const previousPathIds = activeView.type === 'folder' ? activeView.pathIds || [] : [];
        const previousPathNames = activeView.type === 'folder' ? activeView.pathNames || [] : [];

        setActiveView({
            type: 'folder',
            id: folder.id,
            name: folder.name,
            branch: nextBranch,
            pathIds: [...previousPathIds, folder.id],
            pathNames: [...previousPathNames, folder.name],
            topic: folder.topic || null,
        });
    }, [activeView, setActiveView]);

    const handleBack = React.useCallback(() => {
        if (activeView.type === 'folder') {
            const pathIds = activeView.pathIds || [];
            const pathNames = activeView.pathNames || [];

            if (pathIds.length > 1) {
                setActiveView({
                    type: 'folder',
                    id: pathIds[pathIds.length - 2],
                    name: pathNames[pathNames.length - 2],
                    branch: activeView.branch,
                    pathIds: pathIds.slice(0, -1),
                    pathNames: pathNames.slice(0, -1),
                });
                return;
            }

            const presetFolder = SOURCES_PRESET_FOLDERS.find((folder) => folder.id === activeView.branch);
            if (presetFolder) {
                setActiveView({
                    type: 'preset',
                    id: presetFolder.id,
                    name: presetFolder.name,
                    branch: presetFolder.id,
                });
                return;
            }
        }

        setActiveView(SOURCES_ROOT_VIEW);
    }, [activeView, setActiveView]);

    return {
        openCreateFolderDialog,
        handleFolderClick,
        handleBack,
        openFolderMenu,
        dialogs: {
            menu: {
                anchorEl: folderMenuAnchorEl,
                onClose: closeFolderMenu,
                onRename: openRenameDialog,
                onRemove: openRemoveDialog,
            },
            createDialog: {
                open: isCreateDialogOpen,
                onClose: closeCreateFolderDialog,
                onSubmit: handleCreateFolder,
                value: newFolderName,
                onChange: (event) => {
                    setNewFolderName(event.target.value.slice(0, MAX_FOLDER_NAME_LENGTH));
                    if (createFolderError) {
                        setCreateFolderError(null);
                    }
                },
                onKeyDown: (event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCreateFolder();
                    }
                },
                error: createFolderError,
                helperText: `${trimmedFolderName.length}/${MAX_FOLDER_NAME_LENGTH}`,
                isTooLong: isFolderNameTooLong,
                isSubmitting: isCreatingFolder,
                isSubmitDisabled: isCreateFolderDisabled,
                userId,
            },
            renameDialog: {
                open: isRenameDialogOpen,
                onClose: closeRenameDialog,
                onSubmit: handleRenameFolder,
                value: renameFolderName,
                onChange: (event) => {
                    setRenameFolderName(event.target.value.slice(0, MAX_FOLDER_NAME_LENGTH));
                    if (renameFolderError) {
                        setRenameFolderError(null);
                    }
                },
                onKeyDown: (event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        handleRenameFolder();
                    }
                },
                error: renameFolderError,
                helperText: `${trimmedRenameFolderName.length}/${MAX_FOLDER_NAME_LENGTH}`,
                isSubmitting: isRenamingFolder,
                isSubmitDisabled: isRenameFolderDisabled,
            },
            removeDialog: {
                open: isRemoveDialogOpen,
                onClose: closeRemoveDialog,
                onSubmit: handleRemoveFolder,
                folderName: selectedFolder?.name,
                error: removeFolderError,
                isSubmitting: isRemovingFolder,
                isSubmitDisabled: isRemovingFolder || !selectedFolder,
            },
        },
    };
}
